import { AxiosError } from "axios";
import { HttpRequestGet, HttpRequestPost, HttpRequestPut } from "../../service";

export interface ProTeamSubscription {
  ProTeamId: string;
  ProTeamName: string;
  PayAsYouGoPrice?: number;
  HasActiveSubscription: boolean;
  HasCredits: boolean;
  SubscriptionId: string;
  SubscriptionStartDate?: string;
  SubscriptionPrice?: number;
  SubscriptionNumberOfCredits?: number;
  SubscriptionFrequencyName?: string;
  PremiumFee?: number;
	TotalCreditsRemaining?: number;
	TotalCredits?: number;
	RemainingSubscriptionCredits: number;
	RemainingTrancheCredits: number;
	PremiumEnabled: boolean;
	PremiumOnly: boolean;
}

export interface ProOrganisation {
    Id: string;
    Name: string;
    PhoneNumber: string;
    VATNumber: string;
    StripePaymentIdentifier: string;
    StripeCustomerId: string;
    Created: string;
    IsCollectEnabled: boolean;
    MembersCount: number;
}

export interface ProOrganisationMember {
    CustomerId: string;
    FirstName: string;
    LastName: string;
    Email: string;
    PhoneNumber: string;
    ApprovalThreshold?: number;
    RoleId: number;
    RoleName: string;
    Accepted?: string;
}

export interface ProOrganisationDetails {
    Id: string;
    Name: string;
    PhoneNumber: string;
    VATNumber: string;
    StripePaymentIdentifier: string;
    StripeCustomerId: string;
    Created: string;
    IsCollectEnabled: boolean;
    Members: ProOrganisationMember[];
}

export interface ProInvitation {
    Email: string;
    FirstName: string;
    LastName: string;
    InvitationUrl: string;
    IsCollectEnabled: boolean | null;
    AcceptDate: string;
    Created: string;
}

export interface MerchantSubscription {
	MerchantId: string;
	MerchantName: string;
	PayAsYouGoPrice?: number;
	HasActiveSubscription: boolean;
	SubscriptionId: string;
	SubscriptionStartDate?: string;
	SubscriptionPrice?: number;
	SubscriptionNumberOfCredits?: number;
	SubscriptionFrequencyName?: string;
	PremiumFee?: number;
}

export interface ShopSubscription {
    ShopId: string;
	ShopName: string;
	MerchantId: string;
	MerchantName: string;
	PayAsYouGoPrice?: number;
	HasActiveShopSubscription: boolean;
    HasCredits: boolean;
	ShopSubscriptionId?: string;
	ShopSubscriptionActivationDate?: string;
	ShopSubscriptionSuspensionDate?: string;
	HasActiveMerchantSubscription: boolean;
	MerchantSubscriptionId?: string;
	MerchantSubscriptionCreatedDate?: string;
	MerchantSubscriptionArchivedDate?: string;
	SubscriptionFrequency: string;
	SubscriptionPrice?: number;
	SubscriptionStartDate?: string;
	SubscriptionExpiryDate?: string;
	SubscriptionNumberOfCredits?: number;
	SubscriptionCredits?: number;
	TrancheCredits?: number;
	TotalCredits?: number;
	SubscriptionCreditsUsed?: number;
	TrancheCreditsUsed?: number;
	TotalUsed?: number;
	TrancheCreditsRemaining?: number;
	SubscriptionCreditsRemaining?: number;
	TotalCreditsRemaining?: number;
	TotalRows: number;
  PremiumEnabled: boolean;
  PremiumOnly: boolean;
}

export interface ShopSubscriptions {
	Subscriptions: ShopSubscription[];
	Total: number;
}

export interface SubscriptionListResponse<T> {
	Result: T;
}

export function getProSubscriptionList(
	body,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPost(
		`/admin/subscription/pro/list`,
		() => undefined,
		(response: SubscriptionListResponse<ProTeamSubscription>) => success(response.Result),
		(error: any) => failure(error),
		true,
		body
	);
}

export function getProOrganisationsList(
	body,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPost(
		`/admin/pro/list`,
		() => undefined,
		(response: { Result: { Organisations: ProOrganisation[] } }) => success(response.Result),
		(error: any) => failure(error),
		true,
		body
	);
}

export function getProOrganisationDetails(
	id: string,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestGet(
		`/admin/pro/${id}`,
		() => undefined,
		(response: { Result: ProOrganisationDetails } | ProOrganisationDetails) => {
			// Handle both wrapped and unwrapped responses
			const data = (response as any).Result || response;
			success(data);
		},
		(error: any) => failure(error),
		true
	);
}

export function editProOrganisation(
	id: string,
	data: { Name: string; PhoneNumber: string; VATNumber: string; IsCollectEnabled: boolean },
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPut(
		`/admin/pro/${id}/edit`,
		() => undefined,
		(response: any) => success(response),
		(error: any) => failure(error),
		true,
		data
	);
}

export function inviteNewMemberToProOrganisation(
	proTeamId: string,
	data: { FirstName: string; LastName: string; Email: string; ApprovalRequired: boolean; ApprovalThreshold: number },
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPost(
		`/admin/pro/${proTeamId}/user/invite`,
		() => undefined,
		(response: any) => success(response),
		(error: any) => failure(error),
		true,
		data
	);
}

export function archiveProOrganisation(
	id: string,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPut(
		`/admin/pro/${id}/archive`,
		() => undefined,
		(response: any) => success(response),
		(error: any) => failure(error),
		true,
		{},
	);
}

export function getInvites(
	body,
	success: (response: { Invites: ProInvitation[] }) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPost(
		`/admin/pro/list/invited`,
		() => undefined,
		(response: { Result: { Invites: ProInvitation[] } }) => success(response.Result),
		(error: any) => failure(error),
		true,
		body
	);
}

export function inviteNewProTeam(
	data: { Email: string; FirstName: string; LastName: string; IsCollectEnabled: boolean },
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPost(
		`/admin/pro/invite`,
		() => undefined,
		(response: any) => success(response),
		(error: any) => failure(error),
		true,
		data
	);
}

export interface EditProUserData {
	Email: string;
	FirstName: string;
	LastName: string;
	IsManager: boolean;
	ApprovalThreshold: number;
}

export function editTkProUser(
	proTeamId: string,
	userId: string,
	data: EditProUserData,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPut(
		`/admin/pro/${proTeamId}/user/${userId}/edit`,
		() => undefined,
		(response: any) => success(response),
		(error: any) => failure(error),
		true,
		data
	);
}

export function getMerchantSubscriptionList(
	isAdmin: boolean,
	body,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPost(
		`/admin/subscription/merchant/list`,
		() => undefined,
		(response: SubscriptionListResponse<MerchantSubscription>) => success(response.Result),
		(error: any) => failure(error),
		true,
		body
	);
}

export function getMerchantShopSubscriptionsList(
	isAdmin: boolean,
	id: string,
	body: { Page: number; Size: number; Query: string; },
	success: (response: ShopSubscriptions) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPost(
		isAdmin ? `/admin/subscription/merchant/${id}/shops` : "/merchant/subscription/shops",
		() => undefined,
		(response: any) => success(response.Result as ShopSubscriptions),
		(error: any) => failure(error),
		true,
		body
	);
}

export interface ActivateProTeamSubscriptionRequest {
	ProTeamId: string;
	Price: number;
	NumberOfCredits: number;
}

export function activateProSubscription(
	body: ActivateProTeamSubscriptionRequest,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPost(
		`/admin/subscription/pro/activate`,
		() => undefined,
		(response: unknown) => success(response),
		(error: any) => failure(error),
		true,
		body,
	);
}

export interface ActivateMerchantSubscriptionRequest {
	MerchantId: string;
	Price: number;
	NumberOfCredits: number;
}

export function activateMerchantSubscription(
	body: ActivateMerchantSubscriptionRequest,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPost(
		`/admin/subscription/merchant/activate`,
		() => undefined,
		(response: unknown) => success(response),
		(error: any) => failure(error),
		true,
		body,
	);
}

export interface ActivateShopSubscriptionRequest {
	ShopId: string;
	MerchantId: string;
}

export function activateShopSubscription(
	body: ActivateShopSubscriptionRequest,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPost(
		`/admin/subscription/merchant/shop/activate`,
		() => undefined,
		(response: unknown) => success(response),
		(error: any) => failure(error),
		true,
		body,
	);
}

export interface SuspendProTeamSubscriptionRequest {
	ProTeamId: string;
}

export function suspendProTeamSubscription(
	body: SuspendProTeamSubscriptionRequest,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPost(
		`/admin/subscription/pro/suspend`,
		() => undefined,
		(response: unknown) => success(response),
		(error: any) => failure(error),
		true,
		body
	);
}

export interface SuspendMerchantSubscriptionRequest {
	MerchantId: string;
}

export function suspendMerchantSubscription(
	body: SuspendMerchantSubscriptionRequest,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPost(
		`/admin/subscription/merchant/suspend`,
		() => undefined,
		(response: unknown) => success(response),
		(error: any) => failure(error),
		true,
		body
	);
}

export interface SuspendShopSubscriptionRequest {
	ShopId: string;
}

export function suspendShopSubscription(
	body: SuspendShopSubscriptionRequest,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPost(
		`/admin/subscription/merchant/shop/suspend`,
		() => undefined,
		(response: unknown) => success(response),
		(error: any) => failure(error),
		true,
		body
	);
}

type AddProTeamDeliveryTrancheRequestModel =
	{
		ProTeamId: string;
		NumberOfCredits: number;
		Price: number;
	};

export function addProTeamDeliveryTranche(
	body: AddProTeamDeliveryTrancheRequestModel,
	success: (response: unknown) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPost(
		`/admin/subscription/pro/add-tranche`,
		() => undefined,
		(response: unknown) => success(response),
		(error: any) => failure(error),
		true,
		body,
	);
}

type AddShopDeliveryTrancheRequestModel = {
	ShopId: string;
	NumberOfCredits: number;
	Price: number;
};

export function addShopDeliveryTranche(
	body: AddShopDeliveryTrancheRequestModel,
	success: (response: unknown) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPost(
		`/admin/subscription/merchant/shop/add-tranche`,
		() => undefined,
		(response: unknown) => success(response),
		(error: any) => failure(error),
		true,
		body,
	);
}

export function getProSubscription(
	id,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestGet(
		`/admin/subscription/pro/${id}`,
		() => undefined,
		(response: ProTeamSubscription) => success((response as any).Result as ProTeamSubscription),
		(error: any) => failure(error),
		true
	);
}

export function getMerchantSubscription(
  isAdmin: boolean,
	id: string,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestGet(
		isAdmin ? `/admin/subscription/merchant/${id}` : '/merchant/subscription',
		() => undefined,
		(response: MerchantSubscription) => success((response as any).Result as MerchantSubscription),
		(error: any) => failure(error),
		true
	);
}

export function getShopSubscription(
	id: string,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestGet(
		`/admin/subscription/merchant/shop/${id}`,
		() => undefined,
		(response: ShopSubscription) => success((response as any).Result as ShopSubscription),
		(error: any) => failure(error),
		true
	);
}

export interface SetProTeamPayAsYouGoRequest {
	ProTeamId: string;
	Price: number;
}

export interface SetProTeamPayAsYouGoResponse {
	PayAsYouGoPrice: number;
}

export function setProTeamPayAsYouGo(
	body: SetProTeamPayAsYouGoRequest,
	success: (response: SetProTeamPayAsYouGoResponse) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPut(
		`/admin/subscription/pro/pay-as-you-go`,
		() => undefined,
		(response: SetProTeamPayAsYouGoResponse) => success(response),
		(error: any) => failure(error),
		true,
		body,
	);
}

export interface SetMerchantPayAsYouGoRequest {
	MerchantId: string;
	Price: number;
}

export function setMerchantPayAsYouGo(
	body: SetMerchantPayAsYouGoRequest,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPut(
		`/admin/subscription/merchant/pay-as-you-go`,
		() => undefined,
		(response: unknown) => success(response),
		(error: any) => failure(error),
		true,
		body,
	);
}

export interface SetProTeamPremiumFeeRequest {
	ProTeamId: string;
	Fee: number;
}

export function setProTeamPremiumFee(
	body: SetProTeamPremiumFeeRequest,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPut(
		`/admin/subscription/pro/premium-fee`,
		() => undefined,
		(response: unknown) => success(response),
		(error: any) => failure(error),
		true,
		body,
	);
}

export interface SetMerchantPremiumFeeRequest {
	MerchantId: string;
	Fee: number;
}

export function setMerchantPremiumFee(
	body: SetMerchantPremiumFeeRequest,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPut(
		`/admin/subscription/merchant/premium-fee`,
		() => undefined,
		(response: unknown) => success(response),
		(error: any) => failure(error),
		true,
		body,
	);
}

export function shopTogglePremium(
	id: string,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPut(
		`/admin/subscription/merchant/shop/${id}/toggle-premium`,
		() => undefined,
		(response: unknown) => success(response),
		(error: any) => failure(error),
		true,
		{},
	);
}

export function shopTogglePremiumOnly(
	id: string,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPut(
		`/admin/subscription/merchant/shop/${id}/toggle-premium-only`,
		() => undefined,
		(response: unknown) => success(response),
		(error: any) => failure(error),
		true,
		{},
	);
}

export function proTogglePremium(
	id: string,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPut(
		`/admin/subscription/pro/${id}/toggle-premium`,
		() => undefined,
		(response: unknown) => success(response),
		(error: any) => failure(error),
		true,
		{},
	);
}

export function proTogglePremiumOnly(
	id: string,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestPut(
		`/admin/subscription/pro/${id}/toggle-premium-only`,
		() => undefined,
		(response: unknown) => success(response),
		(error: any) => failure(error),
		true,
		{},
	);
}

export function getShopDetail(
	id,
	success: (response: any) => void,
	failure: (error: AxiosError<any>) => void
): void {
	HttpRequestGet(
		`/admin/subscription/merchant/shop/${id}`,
		() => undefined,
		(response: unknown) => success((response as any).Result),
		(error: any) => failure(error),
		true
	);
}